// Sylas — Chains of Corruption: copies and casts the ability of the enemy
// in the opposing slot (or nearest enemy), using Sylas's own stats.

import { physicalDamage, magicDamage } from "../../combat/DamageCalc";
import { effectiveStats } from "../UnitStats";
import { getAbility, hasAbility } from "./index";
import type { Ability, AbilityHit } from "./Ability";

export const sylasChains: Ability = {
  id: "sylasChains",
  displayName: "Chains of Corruption",
  description: "Steals and casts the ability of the enemy in the opposing slot, using Sylas's own stats. Falls back to a 1.0× AD hit.",
  iconId: "SylasW",

  targetShape({ caster, target, combat }) {
    const enemyTeam = caster.team === "player" ? "enemy" : "player";
    const enemies = combat.livingUnitsOf(enemyTeam);
    // Find enemy at same slot as Sylas
    const sameSlot = enemies.find(e => e.pos.q === caster.pos.q);
    const source = sameSlot ?? target;
    if (source.def.abilityId && hasAbility(source.def.abilityId)) {
      try {
        return getAbility(source.def.abilityId).targetShape({ caster, target, combat });
      } catch { /* ignore */ }
    }
    return [target.pos];
  },

  computeHits({ caster, target, combat }): AbilityHit[] {
    const enemyTeam = caster.team === "player" ? "enemy" : "player";
    const enemies = combat.livingUnitsOf(enemyTeam);
    const sameSlot = enemies.find(e => e.pos.q === caster.pos.q);
    const source = sameSlot ?? target;

    if (source.def.abilityId && hasAbility(source.def.abilityId)) {
      try {
        const hits = getAbility(source.def.abilityId).computeHits({ caster, target, combat });
        if (hits.length > 0) return hits;
      } catch { /* ignore edge-case abilities */ }
    }

    // Fallback: magic chains — chains wrap all enemies for 50% damage
    const stats = effectiveStats(caster);
    const primaryDmg = magicDamage(stats.ap * 0.9 + 66);
    const allEnemies = combat.livingUnitsOf(enemyTeam);
    return allEnemies.map((e, i) => ({
      target: e,
      amount: i === 0 ? primaryDmg : magicDamage(stats.ap * 0.45 + 33),
      type: "magical" as const,
    }));
  },
};
